import React, { Fragment, useContext, useLayoutEffect, useReducer, useRef } from 'react';
import { Tooltip, TooltipTrigger, useIsTouchableDevice } from '@philips-internal/oswald-ui-components';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as CloseIcon } from '../../../../resources/assets/DLS_CloseCrossCircle.svg';
import { ReactComponent as CrossIcon } from '../../../../resources/assets/DLS_Cross.svg';
import { FoundationApplicationContext } from '../../../context/index.js';
import { OverflownModulesPopover } from './overflown-modules-popover.js';
const ARROW_ICON_WIDTH_AND_SPACINGS = 32;
const MODULE_CLOSE_ICON_WIDTH = 20;
const MIN_MODULE_WIDTH = 120;
const MIN_MORE_DROPDOWN_WIDTH = 120;
function getMoreDropdownWidth(moreDropdownExpression) {
    const labelElement = document.createElement('div');
    labelElement.className = 'c-pre-more-dropdown';
    labelElement.innerHTML = MessageService.translate(moreDropdownExpression);
    document.body.appendChild(labelElement);
    const labelElementRect = labelElement.getBoundingClientRect();
    const elementWidth = labelElementRect.width + ARROW_ICON_WIDTH_AND_SPACINGS;
    document.body.removeChild(labelElement);
    const isLessThanMinWidth = elementWidth < MIN_MORE_DROPDOWN_WIDTH;
    return isLessThanMinWidth ? MIN_MORE_DROPDOWN_WIDTH : elementWidth;
}
function getOpenModulesStructure(parameters) {
    const { currentModule, modules, modulesContainerRef, moreDropdownExpression } = parameters;
    const { current } = modulesContainerRef;
    const shownModules = [];
    const overflownModules = [];
    if (!current || !modules.length) {
        return { overflownModules, shownModules };
    }
    const moreDropdownWidth = getMoreDropdownWidth(moreDropdownExpression);
    const maxOpenModulesWidth = current.getBoundingClientRect().width - moreDropdownWidth;
    let currentOpenModulesWidth = 0;
    for (const module of modules) {
        const moduleCloseIconWidth = module.closeable ? MODULE_CLOSE_ICON_WIDTH : 0;
        const moduleWidth = MIN_MODULE_WIDTH + moduleCloseIconWidth;
        currentOpenModulesWidth += moduleWidth;
        const hasEnoughSpace = currentOpenModulesWidth < maxOpenModulesWidth;
        if (hasEnoughSpace) {
            shownModules.push(module);
            continue;
        }
        if (module.id !== currentModule?.id) {
            overflownModules.push(module);
            continue;
        }
        const lastModule = shownModules.shift();
        if (!lastModule) {
            continue;
        }
        overflownModules.push(lastModule);
        shownModules.push(module);
    }
    return { overflownModules, shownModules };
}
function openModulesReducer(data, partialData) {
    return {
        ...data,
        ...partialData,
    };
}
const reducerInitialState = {
    overflownModules: [],
    shownModules: [],
};
function OpenModules(props) {
    const applicationContext = useContext(FoundationApplicationContext);
    const { currentModule, modules, moreDropdownExpression, onModuleClick, onModuleCloseIconClick } = props;
    const modulesContainerRef = useRef(null);
    const [moduleReducerState, setModulesReducerState] = useReducer(openModulesReducer, reducerInitialState);
    const isTouchableDevice = useIsTouchableDevice();
    useLayoutEffect(() => {
        const parameters = {
            currentModule,
            modules,
            modulesContainerRef,
            moreDropdownExpression,
        };
        const { overflownModules, shownModules } = getOpenModulesStructure(parameters);
        setModulesReducerState({ overflownModules, shownModules });
    }, [modules, currentModule, moreDropdownExpression, isTouchableDevice]);
    if (isTouchableDevice) {
        return null;
    }
    return (React.createElement("div", { className: "c-application-bar__modules", ref: modulesContainerRef },
        moduleReducerState.shownModules.map((module) => {
            const moduleInformation = applicationContext.getAdditionalModuleInformation(module.id);
            const moduleName = MessageService.translate(module.name);
            const moduleClassName = classNames({
                'c-application-bar__module': true,
                'c-application-bar__module--active': module.id === currentModule?.id,
            });
            return (React.createElement("div", { className: moduleClassName, key: module.id, onClick: () => onModuleClick(module) },
                React.createElement(Tooltip, { triggers: [TooltipTrigger.ELLIPSIS], value: moduleName },
                    React.createElement("div", { className: "c-application-bar__module-title" }, moduleName)),
                moduleInformation?.badge
                    ? React.createElement("div", { className: "c-application-bar__module-badge" }, moduleInformation?.badge)
                    : null,
                module.closeable
                    ? React.createElement(Fragment, null,
                        React.createElement(CrossIcon, { className: "c-application-bar__module-cross-icon" }),
                        React.createElement(CloseIcon, { className: "c-application-bar__module-close-icon", onClick: (event) => onModuleCloseIconClick(event, module) }))
                    : null));
        }),
        React.createElement(OverflownModulesPopover, { currentModule: currentModule, modules: moduleReducerState.overflownModules, moreDropdownExpression: moreDropdownExpression, onModuleClick: onModuleClick })));
}
export { OpenModules, };
//# sourceMappingURL=open-modules.js.map